import { useQuery } from '@tanstack/react-query';

import { IPartnershipListResponse } from '../types/partnership';
import useImperativeRequestWrapper from '@/hooks/useImperativeRequestWrapper';

type ImperativeRequestFn = ReturnType<typeof useImperativeRequestWrapper>[1];

interface IGetAccountPartnershipsParams {
  accountId: string;
  pageSize: number;
  offset: number;
  makeRequest: ImperativeRequestFn;
}

const getAccountPartnerships = async ({
  accountId,
  pageSize,
  offset,
  makeRequest,
}: IGetAccountPartnershipsParams): Promise<IPartnershipListResponse> => {
  const searchParams = new URLSearchParams({
    pageSize: String(pageSize),
    offset: String(offset),
    sortBy: 'updatedAt',
    order: 'DESC',
  });

  const { data, error } = await makeRequest({
    path: `/admin/accounts/${accountId}/partners?${searchParams.toString()}`,
  });

  if (error || !data) {
    throw new Error(error ?? 'Failed to fetch partnerships');
  }

  return data;
};

export const useGetAccountPartnerships = ({
  accountId,
  pageSize,
  offset,
}: {
  accountId: string;
  pageSize: number;
  offset: number;
}) => {
  const [, makeRequest] = useImperativeRequestWrapper('lpVillage');

  return useQuery({
    queryKey: ['account-partnerships', accountId, pageSize, offset],
    queryFn: () =>
      getAccountPartnerships({ accountId, pageSize, offset, makeRequest }),
    enabled: !!accountId,
  });
};
